import React, { useEffect } from "react";
import { Modal, Form, Input, Button, ColorPicker } from "antd";
import FacilityTag from "../../../components/FacilityTag/FacilityTag";
import { isDarkColor } from "../../../../services/helpFunctions/colorLuminance";

// Default (brand-dark) colour for a brand-new facility.
const DEFAULT_COLOR = "#6A113C";

// Curated dark, distinguishable presets. Users may still pick a custom colour,
// but it must pass the darkness check below so the facility box stays readable
// (dark text on a light tint of the same colour).
const DARK_PRESETS = [
  "#6A113C", "#AA1A5F", "#9D174D", "#831843",
  "#B91C1C", "#C2410C", "#B45309", "#92400E",
  "#4D7C0F", "#0F7507", "#045B46", "#0E7490",
  "#155E75", "#1D4ED8", "#325899", "#3B81FE",
  "#5B21B6", "#7B50A3", "#374151", "#1F2937",
];

// ColorPicker returns an AggregationColor object; existing facilities load a
// plain hex string. Normalise both to a "#rrggbb" string.
const toHex = (c) => {
  if (!c) return "";
  if (typeof c === "string") return c;
  return c.toHexString ? c.toHexString() : "";
};

const AddFacilityModal = ({ visible, onCancel, onOk, facility }) => {
  const [form] = Form.useForm();
  const colorValue = Form.useWatch("color", form);
  const nameValue = Form.useWatch("name", form);

  useEffect(() => {
    if (facility) {
      form.setFieldsValue({
        name: facility.name,
        color: facility.color || DEFAULT_COLOR,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ color: DEFAULT_COLOR });
    }
  }, [facility, form, visible]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onOk({ ...values, color: toHex(values.color) });
      form.resetFields();
    });
  };

  return (
    <Modal
      title={facility ? "Edit Facility" : "Add Facility"}
      open={visible}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter facility name" }]}
        >
          <Input />
        </Form.Item>
        <div style={{ display: "flex", gap: 32, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Form.Item
            name="color"
            label="Color"
            tooltip="Pick a dark colour — it becomes the label text on a light tint of the same colour."
            rules={[
              {
                validator: (_, value) => {
                  const hex = toHex(value);
                  if (!hex)
                    return Promise.reject(
                      new Error("Please pick a facility color")
                    );
                  if (!isDarkColor(hex))
                    return Promise.reject(
                      new Error(
                        "Please pick a darker colour — light colours are hard to read."
                      )
                    );
                  return Promise.resolve();
                },
              },
            ]}
          >
            <ColorPicker
              format="hex"
              disabledAlpha
              presets={[{ label: "Recommended (dark)", colors: DARK_PRESETS }]}
              showText
            />
          </Form.Item>

          {/* Live preview of how the facility box will look in the tables —
              placed to the right of the colour picker. */}
          <Form.Item label="Preview">
            <FacilityTag
              name={nameValue || "Facility name"}
              color={toHex(colorValue) || DEFAULT_COLOR}
            />
          </Form.Item>
        </div>

        <Form.Item className="">
          <div className="d-flex justify-content-center gap-3">
            <Button className="inviteButtonTPM" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              className="addButtonTPM"
              type="primary"
              onClick={handleSubmit}
              style={{ marginRight: 8 }}
            >
              {facility ? "Edit Facility" : "Add Facility"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddFacilityModal;
